import { useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { recipesApi }    from '@/api/recipes';
import { Spinner }       from '@/components/ui';
import {
  Thermometer, ArrowLeft, Maximize, RefreshCw, IceCream,
} from 'lucide-react';
import { formatCurrency } from '@/lib/utils';

// Kitchen detail page — optimised for tablets mounted in the kitchen.
// Large typography, high-contrast, fullscreen support, auto-refresh.
export default function KitchenDetailPage() {
  const { t }  = useTranslation();
  const { id } = useParams<{ id: string }>();

  // Auto-refresh every 60 seconds (or from settings).
  // In a full implementation, this would read from app_settings.kitchen_refresh_s
  const REFRESH_MS = 60_000;

  const { data: recipe, isLoading, dataUpdatedAt, refetch } = useQuery({
    queryKey:       ['recipe', id],
    queryFn:        () => recipesApi.get(id!),
    enabled:        !!id,
    refetchInterval: REFRESH_MS,
  });

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {/* ignore */});
    } else {
      document.exitFullscreen().catch(() => {/* ignore */});
    }
  }, []);

  if (isLoading) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Spinner size="lg" className="h-12 w-12" />
    </div>
  );

  if (!recipe) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-background">
      <IceCream size={40} className="text-muted-foreground" />
      <p className="text-xl text-muted-foreground">Recipe not found.</p>
      <Link to="/kitchen" className="text-primary underline">{t('common.back')}</Link>
    </div>
  );

  const calc = recipe.calculations;

  return (
    <div className="kitchen-display min-h-screen bg-background pb-16">
      {/* ── Top bar ─────────────────────────────────────────────────────── */}
      <div className="sticky top-0 z-10 flex items-center justify-between gap-4 border-b bg-card/95 backdrop-blur px-4 py-3 sm:px-6 print:hidden">
        <Link
          to="/kitchen"
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft size={20} />
          <span className="text-base">{t('common.back')}</span>
        </Link>

        <div className="flex items-center gap-2">
          <button
            onClick={() => refetch()}
            className="flex items-center gap-1.5 rounded px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            title={`${t('kitchen.lastRefresh')}: ${new Date(dataUpdatedAt).toLocaleTimeString()}`}
          >
            <RefreshCw size={15} /> {t('kitchen.refresh')}
          </button>
          <button
            onClick={toggleFullscreen}
            className="flex items-center gap-1.5 rounded px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
          >
            <Maximize size={15} /> {t('kitchen.fullscreen')}
          </button>
        </div>
      </div>

      {/* ── Content ─────────────────────────────────────────────────────── */}
      <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6 space-y-8">

        {/* Recipe heading */}
        <div>
          <h1 className="text-4xl sm:text-5xl font-bold leading-tight">{recipe.name}</h1>
          {recipe.recipe_type_name && (
            <p className="mt-2 text-xl text-muted-foreground">{recipe.recipe_type_name}</p>
          )}
        </div>

        {/* Serving temperature — hero element */}
        {calc && (
          <div className={`flex items-center gap-4 rounded-2xl p-5 ${
            calc.suggested_serving_temp === 'PAC ERROR'
              ? 'bg-destructive/10 text-destructive'
              : 'bg-blue-500/10 text-blue-700 dark:text-blue-300'
          }`}>
            <Thermometer size={36} />
            <div>
              <p className="text-lg font-medium text-muted-foreground">{t('recipes.servingTemp')}</p>
              <p className="text-5xl font-black tracking-tight">{calc.suggested_serving_temp}</p>
            </div>
            <div className="ml-auto text-right">
              <p className="text-sm text-muted-foreground">PAC</p>
              <p className="text-3xl font-bold">{calc.total_pac}</p>
            </div>
          </div>
        )}

        {/* Production summary */}
        {calc && (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <StatBox label={t('recipes.totalProduction')} value={`${calc.total_production_grams.toFixed(0)} g`} />
            <StatBox label={t('recipes.portions')} value={String(calc.number_of_portions)} />
            <StatBox label={t('recipes.costPerPortion')} value={formatCurrency(calc.cost_per_portion)} />
            <StatBox label={t('recipes.multiplier')} value={`${recipe.multiplier}×`} />
          </div>
        )}

        {/* Ingredient lines */}
        <div>
          <h2 className="text-3xl font-bold mb-4">{t('recipes.ingredients')}</h2>
          <div className="rounded-lg border overflow-hidden">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-4 text-lg font-semibold">{t('ingredients.name')}</th>
                  <th className="text-right p-4 text-lg font-semibold">{t('recipes.gramsPerKilo')}</th>
                  <th className="text-right p-4 text-lg font-semibold hidden sm:table-cell">{t('recipes.totalGrams')}</th>
                </tr>
              </thead>
              <tbody>
                {recipe.ingredients.map((line, idx) => (
                  <tr
                    key={line.id ?? idx}
                    className={idx % 2 === 0 ? 'bg-background' : 'bg-muted/20'}
                  >
                    <td className="p-4 text-xl font-medium">{line.ingredient_name}</td>
                    <td className="p-4 text-xl text-right font-mono font-semibold">
                      {Number(line.grams_per_kilo).toFixed(1)} g
                    </td>
                    <td className="p-4 text-lg text-right text-muted-foreground font-mono hidden sm:table-cell">
                      {Number(line.total_grams).toFixed(1)} g
                      {Number(line.waste_ratio) > 0 && (
                        <span className="text-sm ml-1">(+{(Number(line.waste_ratio) * 100).toFixed(0)}% waste)</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              {calc && (
                <tfoot className="border-t-2 font-bold bg-card">
                  <tr>
                    <td className="p-4 text-xl">{t('common.total')}</td>
                    <td className="p-4 text-xl text-right font-mono">
                      {recipe.ingredients.reduce((s, l) => s + Number(l.grams_per_kilo), 0).toFixed(1)} g
                    </td>
                    <td className="p-4 hidden sm:table-cell" />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>

        {/* Preparation notes */}
        {recipe.kitchen_notes && (
          <div>
            <h2 className="text-3xl font-bold mb-3">{t('recipes.kitchenNotes')}</h2>
            <div className="rounded-lg border bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800 p-5">
              <p className="text-xl whitespace-pre-wrap leading-relaxed">{recipe.kitchen_notes}</p>
            </div>
          </div>
        )}

        {recipe.preparation_notes && (
          <div>
            <h2 className="text-3xl font-bold mb-3">{t('recipes.preparationNotes')}</h2>
            <p className="text-xl whitespace-pre-wrap leading-relaxed">{recipe.preparation_notes}</p>
          </div>
        )}

        {recipe.presentation_notes && (
          <div>
            <h2 className="text-3xl font-bold mb-3">{t('recipes.presentationNotes')}</h2>
            <p className="text-xl whitespace-pre-wrap leading-relaxed">{recipe.presentation_notes}</p>
          </div>
        )}

        {/* Technical balance (condensed) */}
        {calc && (
          <div>
            <h2 className="text-2xl font-bold mb-3">{t('recipes.technicals')}</h2>
            <div className="grid grid-cols-3 gap-3 sm:grid-cols-7">
              <MiniStat label="Sugars" value={`${calc.total_sugars_pct.toFixed(1)}%`} />
              <MiniStat label="Fat"    value={`${calc.total_fat_pct.toFixed(1)}%`} />
              <MiniStat label="SML"    value={`${calc.total_sml_pct.toFixed(1)}%`} />
              <MiniStat label="SNFM"   value={`${calc.total_solids_diff.toFixed(1)}%`} />
              <MiniStat label="Solids" value={`${calc.total_solids_total.toFixed(1)}%`} />
              <MiniStat label="POD"    value={String(calc.total_pod.toFixed(0))} />
              <MiniStat label="PAC"    value={String(calc.total_pac.toFixed(0))} highlight />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StatBox({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border bg-card p-4 text-center">
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className="text-2xl font-bold mt-1">{value}</p>
    </div>
  );
}

function MiniStat({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className={`rounded-lg border p-2 text-center ${highlight ? 'border-blue-300 bg-blue-50 dark:bg-blue-950/30' : 'bg-muted/30'}`}>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`text-lg font-bold ${highlight ? 'text-blue-700 dark:text-blue-300' : ''}`}>{value}</p>
    </div>
  );
}
